import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime, Subject } from 'rxjs';
import { UserMonitoringService } from 'src/app/core/user-monitoring.service';
import { LoggerService } from '../logger.service';
import {
    CONNECTION_CHANGE_DEBOUNCE_MS,
    CONNECTION_QUALITY_LOG_PREFIX,
    MAXIMUM_LATENCY_MS,
    MINIMUM_DOWNLOAD_SPEED_MBITS,
    RECOMMENDED_DOWNLOAD_SPEED_MBITS,
    RECOMMENDED_LATENCY_MS
} from './connection-quality.constants';
import { ConnectionQuality } from './connection-quality.enums';
import {
    ConnectionMetricEvaluationArgs,
    ConnectionQualityChangeArgs,
    ConnectionQualityResult,
    NavigatorAPI,
    NetworkInformation
} from './connection-quality.models';

/**
 * An injectable Angular service that provides metrics on the quality of the user's internet connection
 */
@UntilDestroy()
@Injectable({
    providedIn: 'root'
})
export class ConnectionQualityService implements OnDestroy {
    private readonly _loggerService = inject(LoggerService);
    private readonly _userMonitoringService = inject(UserMonitoringService);

    // A 'Subject' to stack connection update events before they get debounced and pushed through 'connectionQualitySubject'
    private readonly connectionChangeQueue = new Subject<void>();

    private _connection?: NetworkInformation;

    /**
     * A signal derived from 'connectionQuality$' that can be leveraged for connection quality changes.
     */
    public connectionQualitySig = signal<ConnectionQualityChangeArgs | null>(null);

    initialize() {
        // don't bother if the connection's already been init'd
        if (this._connection) {
            return;
        }

        //initialize the connection
        this._connection = (navigator as NavigatorAPI)?.connection;

        // safeguard against browsers that are not chromium-based
        if (!this._connection) {
            return;
        }

        // Debounce rapid changes to keep from spamming the BehaviorSubject with updates
        // e.g. when the connection is oscillating between Meeting and Exceeding the recommended level
        this.connectionChangeQueue
            .pipe(debounceTime(CONNECTION_CHANGE_DEBOUNCE_MS), untilDestroyed(this))
            .subscribe(() => this.emitConnectionQuality());

        // run a speed check, just in case the change event doesn't ever fire
        this.onConnectionChange();

        // If the API is available, push a value into the subject on every "change" event
        if (this._connection?.addEventListener) {
            this._connection.addEventListener('change', this.onConnectionChange.bind(this));
        }
    }

    ngOnDestroy(): void {
        if (this._connection?.removeEventListener) {
            this._connection.removeEventListener('change', this.onConnectionChange.bind(this));
        }
    }

    private onConnectionChange() {
        this.connectionChangeQueue.next();
    }

    private evaluateConnectionMetric(args: ConnectionMetricEvaluationArgs): ConnectionQualityResult {
        if (args.doesNotMeetMinimum) {
            return {
                connectionQuality: ConnectionQuality.DoesNotMeetMinimumRequirements,
                message: args.doesNotMeetMinimumMessage
            };
        } else if (args.doesNotMeetRecommended) {
            return {
                connectionQuality: ConnectionQuality.DoesNotMeetRecomendedRequirements,
                message: args.doesNotMeetRecommendedMessage
            };
        } else {
            return {
                connectionQuality: ConnectionQuality.ExceedsRecommendedRequirements
            };
        }
    }

    private logConnectionQualityResult(result: ConnectionQualityResult) {
        // if it's good, don't bother
        if (result.connectionQuality === ConnectionQuality.ExceedsRecommendedRequirements) {
            return;
        }
        const loggingMessage = `${CONNECTION_QUALITY_LOG_PREFIX}: ${result.message}`;
        this._loggerService.warn(loggingMessage);
        this._userMonitoringService.addRumAction(loggingMessage);
    }

    private emitConnectionQuality(): void {
        // Ensure connection is defined before accessing properties
        if (!this._connection) {
            return;
        }

        // Destructure the relevant properties from the network information
        const { downlink, rtt } = this._connection;

        // Fetch the downlink quality
        const downloadQuality = this.evaluateConnectionMetric({
            doesNotMeetMinimum: downlink < MINIMUM_DOWNLOAD_SPEED_MBITS,
            doesNotMeetRecommended: downlink < RECOMMENDED_DOWNLOAD_SPEED_MBITS,
            doesNotMeetMinimumMessage: `Download speed is ${downlink}Mbps, which is below the minimum required ${MINIMUM_DOWNLOAD_SPEED_MBITS}Mbps.`,
            doesNotMeetRecommendedMessage: `Download speed is ${downlink}Mbps, which is under the recommended speed of ${RECOMMENDED_DOWNLOAD_SPEED_MBITS}Mbps.`
        });

        // Fetch the latency quality
        const latencyQuality = this.evaluateConnectionMetric({
            doesNotMeetMinimum: rtt > MAXIMUM_LATENCY_MS,
            doesNotMeetRecommended: rtt > RECOMMENDED_LATENCY_MS,
            doesNotMeetMinimumMessage: `Network latency is ${rtt}ms, which slower than the required ${MAXIMUM_LATENCY_MS}`,
            doesNotMeetRecommendedMessage: `Network latency is ${rtt}ms, which slower than the recommended ${RECOMMENDED_LATENCY_MS}ms`
        });

        //log both
        this.logConnectionQualityResult(downloadQuality);
        this.logConnectionQualityResult(latencyQuality);

        //The lowest quality determines the overall reported quality of the connection.
        //If more logic ends up going toward determining the overall reported quality, consider breaking this out into a dedicated private method
        const connectionQuality = Math.min(downloadQuality.connectionQuality, latencyQuality.connectionQuality);

        // Filter out empty/undefined messages
        const messages = [downloadQuality.message, latencyQuality.message].filter((i): i is string => !!i);

        // Emit the connection quality change args
        this.connectionQualitySig.set({
            downloadSpeed: downlink,
            connectionLatency: rtt,
            connectionQuality,
            messages
        });
    }
}
